import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as Slot from "radix-ui/slot";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "./cn";

export const buttonVariants = cva(
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md border font-medium transition-colors duration-120 ease-out disabled:pointer-events-none disabled:opacity-50 aria-busy:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "border-accent bg-accent text-on-accent hover:border-accent-hover hover:bg-accent-hover",
        secondary: "border-border-strong bg-surface text-fg hover:bg-surface-hover",
        ghost: "border-transparent bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg",
        destructive: "border-danger bg-danger text-on-accent hover:opacity-90",
      },
      size: {
        sm: "h-7 px-2.5 text-cell",
        md: "h-9 px-3 text-body",
        icon: "size-8 p-0 text-body",
      },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Shows a spinner, sets aria-busy and blocks clicks while an action is in flight. */
  loading?: boolean;
  /** Render the child element instead of a <button> (links styled as buttons). */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading = false, asChild = false, disabled, children, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
